'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const pathname = usePathname();
  const router = useRouter();
  const resourcesRef = useRef<HTMLLIElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const { user, signOut, loading } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (resourcesRef.current && !resourcesRef.current.contains(e.target as Node)) {
        setResourcesOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignOut = async () => {
    await signOut();
    setUserMenuOpen(false);
    router.push('/');
    router.refresh();
  };

  const navLinks = [
    { label: 'Home', href: '/' },
    { label: 'Offers', href: '/apply' },
    { label: 'About Us', href: '/about' },
    { label: 'Guides', href: '/guides' },
  ];

  const resourceLinks = [
    { label: 'FAQ', href: '/faq' },
    { label: 'Partner with Us', href: '#' },
    { label: 'Privacy Policy', href: '#' },
    { label: 'Terms & Conditions', href: '#' },
  ];

  return (
    <>
      <nav
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          background: '#ffffff',
          borderBottom: '1px solid #e2e8f0',
          height: '68px',
          boxShadow: scrolled ? '0 2px 12px rgba(0,0,0,0.08)' : 'none',
          transition: 'box-shadow 0.3s ease',
        }}
      >
        <div
          style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 60px',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
          className="nav-inner"
        >
          {/* Logo */}
          <Link
            href="/"
            style={{
              fontFamily: 'Merriweather, serif',
              fontWeight: 900,
              fontSize: '1.5rem',
              color: '#1a2b4a',
              textDecoration: 'none',
            }}
          >
            Lendo
          </Link>

          {/* Center Nav */}
          <ul
            className="nav-center"
            style={{
              display: 'flex',
              listStyle: 'none',
              margin: 0,
              padding: 0,
              gap: '32px',
              alignItems: 'center',
            }}
          >
            {navLinks.map((link) => (
              <li key={link.href}>
                <Link
                  href={link.href}
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 500,
                    fontSize: '0.92rem',
                    color: pathname === link.href ? '#00b28a' : '#4a5568',
                    textDecoration: 'none',
                    transition: 'color 0.2s ease',
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = '#00b28a')}
                  onMouseLeave={(e) => (e.currentTarget.style.color = pathname === link.href ? '#00b28a' : '#4a5568')}
                >
                  {link.label}
                </Link>
              </li>
            ))}
            {/* Resources Dropdown */}
            <li ref={resourcesRef} style={{ position: 'relative' }}>
              <button
                onClick={() => setResourcesOpen(!resourcesOpen)}
                onMouseEnter={() => setResourcesOpen(true)}
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                  fontSize: '0.92rem',
                  color: '#4a5568',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: 0,
                  transition: 'color 0.2s ease',
                }}
              >
                Resources
                <span style={{ color: '#00b28a', fontSize: '0.75rem' }}>▾</span>
              </button>
              {resourcesOpen && (
                <div
                  onMouseLeave={() => setResourcesOpen(false)}
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    marginTop: '12px',
                    background: '#ffffff',
                    borderRadius: '10px',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
                    padding: '16px',
                    minWidth: '180px',
                    zIndex: 100,
                  }}
                >
                  {resourceLinks.map((r) => (
                    <Link
                      key={r.label}
                      href={r.href}
                      onClick={() => setResourcesOpen(false)}
                      style={{
                        display: 'block',
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 500,
                        fontSize: '0.88rem',
                        color: '#4a5568',
                        textDecoration: 'none',
                        padding: '8px 0',
                        transition: 'color 0.2s ease',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.color = '#00b28a')}
                      onMouseLeave={(e) => (e.currentTarget.style.color = '#4a5568')}
                    >
                      {r.label}
                    </Link>
                  ))}
                </div>
              )}
            </li>
          </ul>

          {/* Right: Social + CTA / Auth */}
          <div className="nav-right" style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>

            {/* Auth state */}
            {!loading && (
              <>
                {user ? (
                  <div ref={userMenuRef} style={{ position: 'relative' }}>
                    <button
                      onClick={() => setUserMenuOpen(!userMenuOpen)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        background: 'none',
                        border: '1.5px solid #e2e8f0',
                        borderRadius: '50px',
                        padding: '7px 14px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.borderColor = '#00b28a')}
                      onMouseLeave={(e) => (e.currentTarget.style.borderColor = '#e2e8f0')}
                    >
                      <div
                        style={{
                          width: '26px',
                          height: '26px',
                          borderRadius: '50%',
                          background: '#00b28a',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontFamily: 'DM Sans, sans-serif',
                          fontWeight: 700,
                          fontSize: '0.75rem',
                          color: '#ffffff',
                        }}
                      >
                        {(user.email || '?')[0].toUpperCase()}
                      </div>
                      <span style={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '0.88rem', color: '#4a5568' }}>
                        Account
                      </span>
                      <span style={{ color: '#00b28a', fontSize: '0.7rem' }}>▾</span>
                    </button>
                    {userMenuOpen && (
                      <div
                        style={{
                          position: 'absolute',
                          top: 'calc(100% + 10px)',
                          right: 0,
                          background: '#ffffff',
                          borderRadius: '12px',
                          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                          padding: '8px',
                          minWidth: '180px',
                          zIndex: 200,
                          border: '1px solid #e8edf3',
                        }}
                      >
                        <div style={{ padding: '10px 12px 8px', borderBottom: '1px solid #f0f4f8', marginBottom: '4px' }}>
                          <p style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.78rem', color: '#a0aec0', margin: 0 }}>Signed in as</p>
                          <p style={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 600, fontSize: '0.85rem', color: '#1a2b4a', margin: '2px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '160px' }}>
                            {user.email}
                          </p>
                        </div>
                        <Link
                          href="/account"
                          onClick={() => setUserMenuOpen(false)}
                          style={{
                            display: 'block',
                            padding: '9px 12px',
                            fontFamily: 'DM Sans, sans-serif',
                            fontWeight: 500,
                            fontSize: '0.88rem',
                            color: '#4a5568',
                            textDecoration: 'none',
                            borderRadius: '8px',
                            transition: 'background 0.15s',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = '#f7f9fc')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                        >
                          My Account
                        </Link>
                        <Link
                          href="/dashboard"
                          onClick={() => setUserMenuOpen(false)}
                          style={{
                            display: 'block',
                            padding: '9px 12px',
                            fontFamily: 'DM Sans, sans-serif',
                            fontWeight: 500,
                            fontSize: '0.88rem',
                            color: '#4a5568',
                            textDecoration: 'none',
                            borderRadius: '8px',
                            transition: 'background 0.15s',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = '#f7f9fc')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                        >
                          My Dashboard
                        </Link>
                        <Link
                          href="/notifications"
                          onClick={() => setUserMenuOpen(false)}
                          style={{
                            display: 'block',
                            padding: '9px 12px',
                            fontFamily: 'DM Sans, sans-serif',
                            fontWeight: 500,
                            fontSize: '0.88rem',
                            color: '#4a5568',
                            textDecoration: 'none',
                            borderRadius: '8px',
                            transition: 'background 0.15s',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = '#f7f9fc')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                        >
                          Notifications
                        </Link>
                        <Link
                          href="/loans"
                          onClick={() => setUserMenuOpen(false)}
                          style={{
                            display: 'block',
                            padding: '9px 12px',
                            fontFamily: 'DM Sans, sans-serif',
                            fontWeight: 500,
                            fontSize: '0.88rem',
                            color: '#4a5568',
                            textDecoration: 'none',
                            borderRadius: '8px',
                            transition: 'background 0.15s',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = '#f7f9fc')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                        >
                          My Loans
                        </Link>
                        <Link
                          href="/documents"
                          onClick={() => setUserMenuOpen(false)}
                          style={{
                            display: 'block',
                            padding: '9px 12px',
                            fontFamily: 'DM Sans, sans-serif',
                            fontWeight: 500,
                            fontSize: '0.88rem',
                            color: '#4a5568',
                            textDecoration: 'none',
                            borderRadius: '8px',
                            transition: 'background 0.15s',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = '#f7f9fc')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                        >
                          My Documents
                        </Link>
                        <button
                          onClick={handleSignOut}
                          style={{
                            display: 'block',
                            width: '100%',
                            textAlign: 'left',
                            padding: '9px 12px',
                            fontFamily: 'DM Sans, sans-serif',
                            fontWeight: 500,
                            fontSize: '0.88rem',
                            color: '#c53030',
                            background: 'none',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            transition: 'background 0.15s',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = '#fff5f5')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                        >
                          Sign Out
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Link
                      href="/login"
                      style={{
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 500,
                        fontSize: '0.9rem',
                        color: '#4a5568',
                        textDecoration: 'none',
                        transition: 'color 0.2s',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.color = '#00b28a')}
                      onMouseLeave={(e) => (e.currentTarget.style.color = '#4a5568')}
                    >
                      Sign In
                    </Link>
                    <Link
                      href="/apply"
                      style={{
                        background: '#00b28a',
                        color: '#ffffff',
                        borderRadius: '50px',
                        padding: '10px 24px',
                        fontFamily: 'Inter, sans-serif',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        textDecoration: 'none',
                        transition: 'all 0.2s ease',
                        whiteSpace: 'nowrap',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#009970';
                        e.currentTarget.style.transform = 'translateY(-1px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#00b28a';
                        e.currentTarget.style.transform = 'none';
                      }}
                    >
                      Offers
                    </Link>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Hamburger */}
          <button
            className="hamburger"
            onClick={() => setMobileOpen(!mobileOpen)}
            style={{
              display: 'none',
              background: 'none',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: '#1a2b4a',
            }}
            aria-label="Toggle menu"
          >
            {mobileOpen ? '✕' : '☰'}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div
            style={{
              background: '#ffffff',
              borderTop: '1px solid #e2e8f0',
              padding: '16px 24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
            }}
          >
            {navLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setMobileOpen(false)}
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                  fontSize: '1rem',
                  color: '#4a5568',
                  textDecoration: 'none',
                  padding: '12px 0',
                  borderBottom: '1px solid #e2e8f0',
                  display: 'block',
                }}
              >
                {link.label}
              </Link>
            ))}
            {resourceLinks.map((r) => (
              <Link
                key={r.label}
                href={r.href}
                onClick={() => setMobileOpen(false)}
                style={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                  fontSize: '1rem',
                  color: '#4a5568',
                  textDecoration: 'none',
                  padding: '12px 0',
                  borderBottom: '1px solid #e2e8f0',
                  display: 'block',
                }}
              >
                {r.label}
              </Link>
            ))}
            {user ? (
              <>
                <Link
                  href="/account"
                  onClick={() => setMobileOpen(false)}
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 500,
                    fontSize: '1rem',
                    color: '#4a5568',
                    textDecoration: 'none',
                    padding: '12px 0',
                    borderBottom: '1px solid #e2e8f0',
                    display: 'block',
                  }}
                >
                  My Account
                </Link>
                <Link
                  href="/dashboard"
                  onClick={() => setMobileOpen(false)}
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 500,
                    fontSize: '1rem',
                    color: '#4a5568',
                    textDecoration: 'none',
                    padding: '12px 0',
                    borderBottom: '1px solid #e2e8f0',
                    display: 'block',
                  }}
                >
                  My Dashboard
                </Link>
                <Link
                  href="/notifications"
                  onClick={() => setMobileOpen(false)}
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 500,
                    fontSize: '1rem',
                    color: '#4a5568',
                    textDecoration: 'none',
                    padding: '12px 0',
                    borderBottom: '1px solid #e2e8f0',
                    display: 'block',
                  }}
                >
                  Notifications
                </Link>
                <Link
                  href="/loans"
                  onClick={() => setMobileOpen(false)}
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 500,
                    fontSize: '1rem',
                    color: '#4a5568',
                    textDecoration: 'none',
                    padding: '12px 0',
                    borderBottom: '1px solid #e2e8f0',
                    display: 'block',
                  }}
                >
                  My Loans
                </Link>
                <Link
                  href="/documents"
                  onClick={() => setMobileOpen(false)}
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 500,
                    fontSize: '1rem',
                    color: '#4a5568',
                    textDecoration: 'none',
                    padding: '12px 0',
                    borderBottom: '1px solid #e2e8f0',
                    display: 'block',
                  }}
                >
                  My Documents
                </Link>
                <button
                  onClick={() => { handleSignOut(); setMobileOpen(false); }}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 500,
                    fontSize: '1rem',
                    color: '#c53030',
                    textAlign: 'left',
                    padding: '12px 0',
                    cursor: 'pointer',
                  }}
                >
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <Link
                  href="/login"
                  onClick={() => setMobileOpen(false)}
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 500,
                    fontSize: '1rem',
                    color: '#4a5568',
                    textDecoration: 'none',
                    padding: '12px 0',
                    borderBottom: '1px solid #e2e8f0',
                    display: 'block',
                  }}
                >
                  Sign In
                </Link>
                <Link
                  href="/apply"
                  onClick={() => setMobileOpen(false)}
                  style={{
                    background: '#00b28a',
                    color: '#ffffff',
                    borderRadius: '50px',
                    padding: '12px 24px',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 600,
                    fontSize: '0.95rem',
                    textDecoration: 'none',
                    textAlign: 'center',
                    marginTop: '12px',
                    display: 'block',
                  }}
                >
                  Get Offers
                </Link>
              </>
            )}
          </div>
        )}
      </nav>

      <style>{`
        @media (max-width: 768px) {
          .nav-center { display: none !important; }
          .nav-right { display: none !important; }
          .hamburger { display: block !important; }
        }
      `}</style>
    </>
  );
}
